import { Badge, Button, Input } from "@simplicity/ui";
import { X } from "lucide-react";
import { useState } from "react";

interface SchoolIdsInputProps {
  id?: string;
  /** Effective value at this layer (after inheritance). May be empty. */
  value: string[];
  /** Active override at THIS layer, distinct from the inherited effective value. */
  override?: string[] | undefined;
  /** Optional inheritance source — when set and `override` is undefined, the
   *  chips render dimmed with a `(from <inheritedFrom>)` annotation. */
  inheritedFrom?: "project" | "table" | null;
  onChange: (next: string[]) => void;
  /** Disable the input (e.g. when the parent has set `unfiltered: true`). */
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Multi-value chip input for PowerSchool `vars.filters.schoolids`. Comma or Enter commits a chip, X
 * removes one. When `override` is undefined and `inheritedFrom` is set, the effective value shows
 * dimmed with a `(from <layer>)` annotation, and typing into the field starts an active override at
 * this layer.
 */
export function SchoolIdsInput(props: SchoolIdsInputProps) {
  const { value, override, inheritedFrom, onChange, disabled, id, placeholder } = props;
  const [draft, setDraft] = useState("");

  const isOverride = override !== undefined;
  const dimmed = !isOverride && inheritedFrom != null;

  function commit(raw: string) {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    const next = [...(override ?? value), ...parts];
    // dedupe while preserving order
    const seen = new Set<string>();
    const unique = next.filter((id) => (seen.has(id) ? false : (seen.add(id), true)));
    onChange(unique);
    setDraft("");
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && override && override.length > 0) {
      e.preventDefault();
      onChange(override.slice(0, -1));
    }
  }

  function handleBlur() {
    if (draft.trim()) commit(draft);
  }

  function removeAt(idx: number) {
    if (!override) return;
    onChange(override.filter((_, i) => i !== idx));
  }

  const displayed = isOverride ? override : value;

  return (
    <div data-testid="school-ids-input">
      <div
        className={
          "flex flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1.5 " +
          (dimmed ? "text-muted-foreground italic" : "")
        }
      >
        {displayed.map((sid, idx) => (
          <Badge
            key={`${sid}-${idx}`}
            variant={dimmed ? "outline" : "secondary"}
            className="font-mono text-xs"
          >
            {sid}
            {isOverride && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-1 h-auto p-0.5"
                onClick={() => removeAt(idx)}
                aria-label={`Remove ${sid}`}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </Badge>
        ))}
        <Input
          id={id}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={handleBlur}
          placeholder={
            placeholder ?? (displayed.length === 0 ? "Add school numbers (Enter or comma)…" : "")
          }
          disabled={disabled}
          className="h-7 min-w-[12ch] flex-1 border-0 bg-transparent p-0 font-mono text-xs shadow-none focus-visible:ring-0"
        />
      </div>
      {dimmed && inheritedFrom && (
        <p className="mt-1 text-xs text-muted-foreground italic">(from {inheritedFrom})</p>
      )}
    </div>
  );
}
